/**
 * orgBlock.ts — zaklep organizacije v telesu odgovora (T1192)
 * ===========================================================
 *
 * Backend od T1162/T1178 vrača **dve** strojno berljivi kodi s statusom 403.
 * Nista sopomenki — razlikujeta se v tem, kaj naj uporabnik naredi naslednje:
 *
 * | koda                     | razlog                         | pot ven                 |
 * |--------------------------|--------------------------------|-------------------------|
 * | `ORGANIZATION_SUSPENDED` | `is_active=False` (chargeback) | **plačilo** — celoten   |
 * |                          |                                | `billing` je odprt      |
 * | `ORGANIZATION_SUSPENDED` | `is_active=False` (sysadminov  | **podpora** — plačilna  |
 * |                          | zaklep zaradi zlorabe)         | pot je zaprta (T1351)   |
 * | `ORGANIZATION_CLOSED`    | `deleted_at` — račun je zaprl  | **obnovitev** prek      |
 * |                          | lastnik                        | podpore; billing zaprt  |
 *
 * Telo nosi `{ error, detail, code, organization: { id, name, slug } }`.
 * Organizacija je v telesu **namenoma**: ob zaklepu so organizacijski endpointi
 * zaprti, zato frontend drugače nima iz česa izrisati zaslona.
 *
 * Ta modul je nalašč brez odvisnosti (ne uvaža ne axios ne zustand), da ga
 * lahko uvozita tako API plast kot store, brez krožnega uvoza.
 */

export const ORG_SUSPENDED_CODE = 'ORGANIZATION_SUSPENDED';
export const ORG_CLOSED_CODE = 'ORGANIZATION_CLOSED';

export type OrgBlockCode = typeof ORG_SUSPENDED_CODE | typeof ORG_CLOSED_CODE;

/**
 * Zakaj je organizacija suspendirana (T1351 v zaledju, T1396 tu).
 *
 * Koda odgovora je pri obeh ista (`ORGANIZATION_SUSPENDED`), ker gre za isto
 * stanje in isti zaslon — razlikuje se **pot ven**:
 *
 * | vrednost      | pomen                        | plačilna pot |
 * |---------------|------------------------------|--------------|
 * | `chargeback`  | povratna bremenitev, dolg    | odprta       |
 * | `sysadmin`    | zaklep platforme ob zlorabi  | **zaprta**   |
 *
 * `null` pomeni »ne vem«: pri `ORGANIZATION_CLOSED` (tam suspenzije ni v igri),
 * pri starejšem zaledju in pri zaklepu po WS poti, kjer telesa ni. Obravnava
 * ostane pri milejši različici (plačilo ponujeno) — natanko kot zaledna
 * `suspension_reason_of()`, ki prazno vrednost normalizira v `chargeback`.
 * Zaostritev je zavestno dejanje sysadmina, ne stranski učinek deploya.
 */
export type SuspensionReason = 'chargeback' | 'sysadmin';

const SUSPENSION_REASONS: readonly string[] = ['chargeback', 'sysadmin'];

/**
 * Identiteta organizacije, kot jo vrne telo napake.
 *
 * T1195 je nabor razširil s `country`, `role` in `my_permissions`, ker
 * `/billing` med suspenzijo nima nobenega drugega vira: `country` določa
 * privzeto plačilno metodo (NL → iDEAL, BE → Bancontact), `my_permissions` pa
 * `canManageBilling` — brez njih bi se **lastniku** zaklenjene organizacije
 * izpisalo, da module lahko naroči samo lastnik.
 *
 * Vrednosti, ki jih telo ne nosi, so `''` / `null` / `[]` — »ne vem«, nikoli
 * privzetek. Ugibanje bi pomenilo napačno predlagano plačilo.
 */
export interface BlockedOrganization {
  id: number;
  name: string;
  slug: string;
  country: string;
  role: string | null;
  my_permissions: string[];
  /**
   * T2798 — zaledje prizna `billing.manage` (lastnik ali administrator).
   * Samo pravi `boolean` šteje; vse drugo (odsotnost, `"true"`) je
   * `undefined` in `usePermissions().canManageBilling` pade na mejo pred
   * T2798 (samo lastnik) — nikoli si pravice ne izmisli.
   */
  can_manage_billing?: boolean;
  /**
   * T1396: razlog suspenzije — edini podatek, iz katerega zaslon ve, ali sme
   * ponuditi plačilo. Pri `ORGANIZATION_CLOSED` in pri neznani/manjkajoči
   * vrednosti je `null`; gl. `SuspensionReason`.
   */
  suspension_reason: SuspensionReason | null;
  /**
   * T1381/T1422: sme zaslon ponuditi gumb »Ponovno odpri račun«?
   *
   * Zaledje jo izračuna kot `code == CLOSED_CODE and reopen_is_available(org)`
   * (`apps/core/suspension.py::blocked_payload`), zato je `false` pri
   * suspenziji **in** pri anonimizirani zaprti organizaciji, kjer bi obnovitev
   * vrnila prazen račun.
   *
   * Odsotnost polja (starejše zaledje, WS pot brez telesa) je `false`. Tu je
   * fail-closed pravilna smer, nasprotno kot pri `suspension_reason`: tam
   * neznano pomeni **ne odvzemi** obstoječe poti ven, tu pa neznano pomeni
   * **ne izmisli si** poti, ki je zaledje ni ponudilo — gumb, ki vrne 400, ni
   * pot ven, je slepa ulica z dodatnim klikom.
   */
  can_request_reopen: boolean;
}

export interface OrgBlock {
  code: OrgBlockCode;
  /** Sporočilo strežnika; prazen niz, kadar ga telo nima. */
  message: string;
  /** `null`, kadar organizacije ni bilo mogoče določiti (obramba v globino). */
  organization: BlockedOrganization | null;
}

const BLOCK_CODES: readonly string[] = [ORG_SUSPENDED_CODE, ORG_CLOSED_CODE];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function readOrganization(value: unknown): BlockedOrganization | null {
  if (!isRecord(value)) return null;
  const {
    id,
    name,
    slug,
    country,
    role,
    my_permissions: perms,
    can_manage_billing: canManageBilling,
    suspension_reason: reason,
    can_request_reopen: canReopen,
  } = value;
  if (typeof id !== 'number' || typeof name !== 'string' || typeof slug !== 'string') {
    return null;
  }
  return {
    id,
    name,
    slug,
    // Identiteta (id/name/slug) je obvezna — brez nje zaslona ni. Ostalo je
    // dodatek: če ga starejši backend ne pošlje, ostane prazno in odjemalec
    // ravna, kot da ne ve — nikoli si vrednosti ne izmisli.
    country: typeof country === 'string' ? country : '',
    role: typeof role === 'string' ? role : null,
    my_permissions: Array.isArray(perms)
      ? perms.filter((p): p is string => typeof p === 'string')
      : [],
    can_manage_billing: typeof canManageBilling === 'boolean' ? canManageBilling : undefined,
    // Neznana vrednost (razlog iz novejše različice po vrnitvi nazaj) pade v
    // `null` iz istega razloga kot manjkajoča: milejša obravnava je edina, ki
    // ne odvzame plačilne poti nekomu, ki mu je nihče ni nameraval odvzeti.
    suspension_reason:
      typeof reason === 'string' && SUSPENSION_REASONS.includes(reason)
        ? (reason as SuspensionReason)
        : null,
    // Samo `true` je `true`: niz `"true"` ali `1` iz kakega posrednika ne sme
    // odpreti gumba, ki ga zaledje ni ponudilo.
    can_request_reopen: canReopen === true,
  };
}

/**
 * Prepoznaj zaklep organizacije v (axios) napaki, sicer vrni `null`.
 *
 * Zahtevamo **oboje**: status 403 in znano kodo. Sama koda ne zadošča — 403 s
 * `CSRF_INVALID` ali navadna zavrnitev pravic ne smeta prevzeti aplikacije,
 * sicer zaslon »organizacija je zaklenjena« skrije prave napake.
 */
export function extractOrgBlock(err: unknown): OrgBlock | null {
  if (!isRecord(err)) return null;
  const response = err.response;
  if (!isRecord(response) || response.status !== 403) return null;

  const data = response.data;
  if (!isRecord(data)) return null;

  const code = data.code;
  if (typeof code !== 'string' || !BLOCK_CODES.includes(code)) return null;

  const message =
    (typeof data.error === 'string' && data.error) ||
    (typeof data.detail === 'string' && data.detail) ||
    '';

  return {
    code: code as OrgBlockCode,
    message,
    organization: readOrganization(data.organization),
  };
}

/* ─────────────────────────────────────────────────────────────────────────────
 * T1629 — kdaj sme celozaslonski prevzem izpustiti plačilno stran
 * ───────────────────────────────────────────────────────────────────────────*/

/**
 * Poti, na katerih je plačilo pot iz zaklepa (`App.tsx`).
 *
 * `/billing/success` je tu, ker je to povratna pot iz Mollie: brez nje bi se
 * uporabniku, ki je pravkar plačal, zaslon vrnil čez prevzem, še preden webhook
 * odklene organizacijo.
 */
const BILLING_PATHS: readonly string[] = ['/billing', '/billing/success'];

/**
 * Sme aplikacija ob zaklepu *block* na poti *pathname* ostati priklopljena?
 *
 * Merilo je isto kot zaledno (`apps/core/suspension.py::is_exempt_view`), ker
 * je vsako razhajanje med njima vidno kot napačno sporočilo:
 *
 * * `ORGANIZATION_CLOSED` — izjeme ni, `billing` je zaprt tudi na zaledju;
 * * `ORGANIZATION_SUSPENDED` + `sysadmin` — izjeme **ni** (T1351: organizacija,
 *   zaklenjena zaradi zlorabe, se ne odkupi z denarjem);
 * * `ORGANIZATION_SUSPENDED` + `chargeback`/neznano — izjema velja.
 *
 * Neznan razlog (`null`: WS pot brez telesa, starejše zaledje) je namenoma na
 * milejši strani — zaledna ``suspension_reason_of()`` prazno vrednost
 * normalizira v ``chargeback`` in billing v resnici pusti odprt. Strožje
 * ugibanje bi plačilno pot odvzelo nekomu, ki mu je nihče ni nameraval.
 *
 * Do T1629 je bilo merilo samo `code === ORGANIZATION_SUSPENDED`. Posledica:
 * pri sysadminovem suspenzu se je `/billing` izrisal, vseh 13 klicev je vrnilo
 * 403 in stran je padla na splošen nadomestek »potrebujete pravice lastnika« —
 * lastniku organizacije. Izmerjeno 12.9.2026, ročna verifikacija §2.5 A6.
 */
export function blockAllowsBilling(block: OrgBlock | null, pathname: string): boolean {
  if (!block || block.code !== ORG_SUSPENDED_CODE) return false;
  if (block.organization?.suspension_reason === 'sysadmin') return false;
  return BILLING_PATHS.includes(pathname);
}

type OrgBlockHandler = (block: OrgBlock) => void;

let _handler: OrgBlockHandler | null = null;

/**
 * Registriraj sprejemnika (to počne `orgBlockStore`). Enak vzorec kot
 * `registerAuthFailureHandler` v `services/api.ts`: API plast ne sme uvažati
 * React stanja, sicer nastane krožni uvoz.
 */
export function registerOrgBlockHandler(fn: OrgBlockHandler | null) {
  _handler = fn;
}

/** Javi zaklep. Brez registriranega sprejemnika je klic tih — API plast zaradi
 * zaslona nikoli ne sme pasti. */
export function reportOrgBlock(block: OrgBlock) {
  _handler?.(block);
}

/* ─────────────────────────────────────────────────────────────────────────────
 * T1255 — ista zavrnitev po WebSocket poti
 * ───────────────────────────────────────────────────────────────────────────*/

/**
 * Terminalne WS close kode — zrcalo `src/backend/apps/core/ws_suspension.py`.
 *
 * WS nima statusnih kod, zato razlog nosi close koda v aplikacijskem obsegu
 * (4000–4999). Zaledje povezavo zapre **pred** `accept()` in v docstringu
 * izrecno pravi »ne poskušaj znova« — za 4003/4004/4005 je zavrnitev trajna, ker je
 * odvisna od stanja računa ali pravic, ne od omrežja:
 *
 * | koda | pomen                    | naslednji poskus ima smisel? |
 * |------|--------------------------|------------------------------|
 * | 4001 | avtentikacija            | da — po obnovi seje (T2689)  |
 * | 4003 | ni dostopa               | ne                           |
 * | 4004 | `ORGANIZATION_SUSPENDED` | ne — pot ven je plačilo      |
 * | 4005 | `ORGANIZATION_CLOSED`    | ne — pot ven je podpora      |
 *
 * Nasprotno so 1006 (izpad), 1001, 1012 … prehodne in ponovni poskus je edino
 * pravilno ravnanje — zato je nabor **bela lista**, ne »vse nad 4000«.
 *
 * Seznam živi tu in samo tu: oba odjemalca (`useNotifications`,
 * `useProjectWebsocket`) ga uvozita. Podvojen seznam v dveh datotekah je vzorec,
 * ki v tem projektu redno razpade — ena datoteka dobi novo kodo, druga ne.
 */
export const WS_CLOSE_AUTH_FAILED = 4001;
export const WS_CLOSE_ACCESS_DENIED = 4003;
export const WS_CLOSE_ORG_SUSPENDED = 4004;
export const WS_CLOSE_ORG_CLOSED = 4005;

// T2689: 4001 NI terminalna. Kanal obvestil z njo zapre povezavo ob izteku
// dostopnega žetona (T382, po `accept()` — koda pride do brskalnika); klicatelj
// jo obravnava prek `lib/wsSession.ts` (obnova seje, nato ponovna povezava).
export const WS_TERMINAL_CLOSE_CODES: readonly number[] = [
  WS_CLOSE_ACCESS_DENIED,
  WS_CLOSE_ORG_SUSPENDED,
  WS_CLOSE_ORG_CLOSED,
];

/** WS close koda → strojna koda zaklepa; le dve od štirih sta zaklep organizacije. */
const WS_CLOSE_TO_BLOCK_CODE: Record<number, OrgBlockCode> = {
  [WS_CLOSE_ORG_SUSPENDED]: ORG_SUSPENDED_CODE,
  [WS_CLOSE_ORG_CLOSED]: ORG_CLOSED_CODE,
};

/** Ali je close koda trajna zavrnitev (ne poskušaj znova)? */
export function isTerminalWsCloseCode(code: number | undefined): boolean {
  return typeof code === 'number' && WS_TERMINAL_CLOSE_CODES.includes(code);
}

/**
 * Obravnavaj zaprtje WS povezave. Vrne `true`, kadar je bila koda terminalna —
 * takrat klicatelj **ne sme** načrtovati ponovnega poskusa.
 *
 * Ob 4004/4005 hkrati javi zaklep v isti mehanizem kot HTTP 403
 * (`reportOrgBlock` → `orgBlockStore` → `OrganizationBlockedScreen`). Ločenega
 * zaslona za WS namenoma ni: uporabnik z odprtim urejevalnikom bi sicer videl
 * dve različni razlagi istega stanja.
 *
 * `organization` je `null`, ker close dogodek telesa nima — koda je vse, kar
 * imamo. Zaslon to zdrži (ime organizacije je pri njem pogojno), `orgBlockStore`
 * pa tega zapisa ne pusti povoziti bogatejšega iz telesa 403.
 */
export function handleWsClose(event: { code?: number } | undefined): boolean {
  const code = event?.code;
  if (!isTerminalWsCloseCode(code)) return false;

  const blockCode = WS_CLOSE_TO_BLOCK_CODE[code as number];
  if (blockCode) {
    reportOrgBlock({ code: blockCode, message: '', organization: null });
  }
  return true;
}
